/**
 * O que cabe, mobile (spec 0131 / ADR-098).
 * Enquanto o palco está grudado, o scroll vertical desloca a foto
 * da esquerda até o canto direito.
 *
 * Motor:
 * 1. ViewTimeline (Chrome 115+, Safari 26+): a animação roda no compositor,
 *    sem JS por frame. Fica fluida mesmo com a main thread ocupada.
 * 2. Fallback rAF: transform escrito direto no <img>, sem leitura de
 *    getComputedStyle por frame.
 *
 * Pista (`travel`) = overflow × TRAVEL_RATIO. Um flick no iOS percorre
 * muitos px; pista curta faz a foto "pular pro final".
 */
const GM_WHAT_FITS_TRAVEL_RATIO = 1.8;
const GM_WHAT_FITS_MIN_TRAVEL_VH = 1.1;

function gmWhatFitsClamp(v, min, max) {
  return v < min ? min : v > max ? max : v;
}

function gmWhatFitsPx(el, name, fallback) {
  const raw = getComputedStyle(el).getPropertyValue(name);
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : fallback;
}

/** true se nenhum ancestral vira scroll container (ViewTimeline precisa do root). */
function gmWhatFitsRootScroller(el) {
  for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
    const o = getComputedStyle(n);
    if (/(auto|scroll|hidden)/.test(o.overflowY + o.overflowX)) return false;
  }
  return true;
}

if (!customElements.get('gm-what-fits')) {
  class GmWhatFits extends HTMLElement {
    connectedCallback() {
      this._mq = window.matchMedia('(max-width: 749px)');
      this._reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._sticky = this.querySelector('.gm-what-fits__sticky');
      this._img = this.querySelector('.gm-feito-banner__image');
      this._overflow = 0;
      this._travel = 0;
      this._header = 0;
      this._raf = 0;
      this._anim = null;
      this._key = '';
      this._lastW = window.innerWidth;
      this._useTimeline = typeof window.ViewTimeline === 'function';

      this._onScroll = () => {
        if (this._anim || this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = 0;
          this._update();
        });
      };

      this._measure = () => this._layout();

      // iOS dispara resize quando a barra de URL some/volta. O palco usa svh
      // (estável), então só a largura importa aqui.
      this._onResize = () => {
        if (window.innerWidth === this._lastW) return;
        this._lastW = window.innerWidth;
        this._layout();
      };

      this._onImgLoad = () => {
        this._layout();
        // Decodifica fora da main thread antes do usuário chegar ao palco.
        this._img?.decode?.().catch(() => {});
      };

      this._layout();
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
      window.addEventListener('gm-chrome-change', this._measure);
      this._mq.addEventListener('change', this._measure);
      this._reduced.addEventListener('change', this._measure);

      if (this._img) {
        if (this._img.complete && this._img.naturalWidth) this._onImgLoad();
        else this._img.addEventListener('load', this._onImgLoad, { once: true });
        this._prefetch();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('gm-chrome-change', this._measure);
      this._mq?.removeEventListener('change', this._measure);
      this._reduced?.removeEventListener('change', this._measure);
      this._io?.disconnect();
      if (this._raf) cancelAnimationFrame(this._raf);
      this._stopAnim();
    }

    /** Troca lazy → eager ~2 telas antes, pra foto chegar decodificada. */
    _prefetch() {
      if (this._img.loading !== 'lazy' || !('IntersectionObserver' in window)) return;
      this._io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          this._img.loading = 'eager';
          this._io.disconnect();
        },
        { rootMargin: '200% 0px' }
      );
      this._io.observe(this);
    }

    _reset() {
      this._overflow = 0;
      this._travel = 0;
      this._key = '';
      this._stopAnim();
      this.classList.remove('is-pan');
      this.style.removeProperty('--gm-what-fits-travel');
      if (this._img) this._img.style.removeProperty('transform');
    }

    _stopAnim() {
      if (this._anim) {
        this._anim.cancel();
        this._anim = null;
      }
    }

    _layout() {
      /* ADR-099: rotação estática. O pan (spec 0131) volta sem esta classe. */
      if (this.classList.contains('gm-what-fits--rotate')) {
        this._reset();
        return;
      }

      const mobile = this._mq.matches && !this._reduced.matches;
      const visible = this.getClientRects().length > 0;

      if (!mobile || !visible || !this._img || !this._sticky) {
        this._reset();
        return;
      }

      const nw = this._img.naturalWidth || Number(this._img.getAttribute('width')) || 0;
      const nh = this._img.naturalHeight || Number(this._img.getAttribute('height')) || 0;
      const stage = this._sticky.clientHeight;
      const stageW = this.clientWidth;
      if (!nw || !nh || stageW < 40 || stage < 40) return;

      const overflow = Math.max(0, stage * (nw / nh) - stageW);
      if (overflow < 12) {
        this._reset();
        return;
      }

      const header = gmWhatFitsPx(document.body, '--header-height', 60);
      const travel = Math.round(
        Math.max(
          overflow * GM_WHAT_FITS_TRAVEL_RATIO,
          stage * GM_WHAT_FITS_MIN_TRAVEL_VH
        )
      );

      // gm-chrome-change dispara a cada scroll com a gaveta visível:
      // sem mudança real, não mexe em nada.
      const key = `${Math.round(overflow)}|${travel}|${header}`;
      if (key === this._key) return;
      this._key = key;

      this._overflow = overflow;
      this._travel = travel;
      this._header = header;
      this.style.setProperty('--gm-what-fits-travel', `${travel}px`);
      this.classList.add('is-pan');

      this._stopAnim();
      if (this._useTimeline && gmWhatFitsRootScroller(this)) {
        this._startTimeline();
      }
      if (!this._anim) this._update();
    }

    /**
     * exit-crossing 0% = topo do host no topo da viewport. O palco gruda
     * em top = header, então começa `header` px antes e anda `travel` px.
     * Não depende da altura da viewport (barra do iOS não interfere).
     */
    _startTimeline() {
      try {
        const timeline = new ViewTimeline({ subject: this, axis: 'block' });
        this._img.style.removeProperty('transform');
        this._anim = this._img.animate(
          [
            { transform: 'translate3d(0, 0, 0)' },
            { transform: `translate3d(${-this._overflow}px, 0, 0)` },
          ],
          {
            timeline,
            rangeStart: `exit-crossing ${-this._header}px`,
            rangeEnd: `exit-crossing ${this._travel - this._header}px`,
            fill: 'both',
            easing: 'linear',
          }
        );
      } catch (e) {
        this._stopAnim();
        this._useTimeline = false;
      }
    }

    _update() {
      if (this._anim || this._travel <= 0 || !this._img) return;

      const scrolled = this._header - this.getBoundingClientRect().top;
      const p = gmWhatFitsClamp(scrolled / this._travel, 0, 1);
      const x = -(p * this._overflow).toFixed(2);
      this._img.style.transform = `translate3d(${x}px, 0, 0)`;
    }
  }

  customElements.define('gm-what-fits', GmWhatFits);
}
